/**
 * Product Display & Inline Editing Helper (Paket P)
 *
 * Provides:
 * - Constants (lengths, German UI texts from SPEC-P §12)
 * - Codepoint counting (for multi-byte Unicode / emojis)
 * - Text normalization (NFC, whitespace, control chars)
 * - Teaser generation (F4 rule)
 * - API error mapping to stable German UI texts
 * - Client API functions for Admin Product Display
 */

import { apiFetch } from './api.js';

export const DISPLAY_NAME_MAX = 80;
export const DESCRIPTION_MAX = 5000;
export const TEASER_MAX = 160;

/**
 * German UI Texts (SPEC-P §12 — verbindlich)
 */
export const PRODUCT_EDIT_TEXTS = {
  LABEL_NAME: 'Produktname',
  LABEL_DESCRIPTION: 'Produktbeschreibung',
  EDIT_TOOLTIP_NAME: 'Produktname bearbeiten',
  EDIT_TOOLTIP_DESCRIPTION: 'Produktbeschreibung bearbeiten',
  OVERRIDE_BADGE: 'Angepasst',
  OVERRIDE_BADGE_ADMIN: 'Überschrieben',
  OVERRIDE_BADGE_TOOLTIP: 'Dieser Text wurde für diesen Shop angepasst.',
  PLACEHOLDER_NAME: 'Produktname eingeben (leer lassen = Originalname)',
  PLACEHOLDER_DESCRIPTION: 'Produktbeschreibung eingeben (leer lassen = Originaltext)',
  COUNTER_NAME: (n) => `${n} / 80 Zeichen`,
  COUNTER_DESCRIPTION: (n) => `${n} / 5.000 Zeichen`,
  ORIGINAL_LABEL: (text) => `Original: ${text}`,
  ORIGINAL_SHOW: 'Originaltext anzeigen',
  ORIGINAL_HIDE: 'Originaltext ausblenden',
  KEY_HINT_NAME: 'Enter speichert, Esc bricht ab.',
  KEY_HINT_DESCRIPTION: 'Strg/Cmd + Enter speichert, Esc bricht ab.',
  BTN_SAVE: 'Speichern',
  BTN_CANCEL: 'Abbrechen',
  BTN_RESET: 'Auf Standard zurücksetzen',
  BTN_RESTORE_ORIGINAL: 'Original wiederherstellen',
  BTN_RESET_CONFIRM_TITLE: 'Wirklich zurücksetzen?',
  BTN_RESET_CONFIRM_YES: 'Ja, zurücksetzen',
  STATE_LOADING: 'Lädt …',
  STATE_SAVING: 'Speichert …',
  STATE_SAVED: 'Gespeichert.',
  STATE_RESET: 'Auf Standard zurückgesetzt.',
  STATE_RESTORED: 'Original erfolgreich wiederhergestellt.',
  ERR_NAME_TOO_LONG: 'Der Produktname darf höchstens 80 Zeichen lang sein.',
  ERR_DESCRIPTION_TOO_LONG: 'Die Beschreibung darf höchstens 5.000 Zeichen lang sein.',
  ERR_HTML_NOT_ALLOWED: 'Bitte keinen HTML-Code eingeben (Zeichen wie <b> sind nicht erlaubt).',
  ERR_401: 'Die Sitzung ist abgelaufen. Bitte erneut anmelden.',
  ERR_403: 'Dieses Produkt darf mit diesem Zugang nicht bearbeitet werden.',
  ERR_404: 'Dieses Produkt wurde nicht gefunden. Bitte die Seite neu laden.',
  ERR_405: 'Diese Aktion wird vom Server nicht unterstützt.',
  ERR_408: 'Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.',
  ERR_409: 'Änderungskonflikt: Das Produkt wurde zwischenzeitlich aktualisiert.',
  ERR_413: 'Die Datenmenge ist zu groß für die Übertragung.',
  ERR_415: 'Dieses Datenformat wird nicht unterstützt.',
  ERR_422: 'Die Eingabe konnte nicht verarbeitet werden. Bitte prüfen Sie Ihre Angaben.',
  ERR_429: 'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment.',
  ERR_400_DEFAULT: 'Die Änderung konnte nicht gespeichert werden. Bitte die Seite neu laden.',
  ERR_NETWORK: 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
  ERR_ORIGINAL_UNAVAILABLE: 'Originaltext konnte nicht geladen werden.',
};

/**
 * Counts unicode codepoints accurately (an emoji counts as 1).
 */
export function countCodepoints(str) {
  if (!str) return 0;
  return Array.from(str).length;
}

/**
 * Normalizes text for client editing / submission (SPEC-P §10).
 */
export function normalizeClientText(field, raw) {
  if (raw === null || raw === undefined) return null;
  let str = String(raw).normalize('NFC').replace(/\r\n|\r/g, '\n');
  // Strip control characters (except \n) and bidi/zero-width formatting characters
  str = str.replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F\u200B\u200E\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '');

  if (field === 'name' || field === 'displayName') {
    str = str.replace(/\s+/g, ' ').trim();
  } else {
    str = str.trim().replace(/\n{3,}/g, '\n\n');
  }

  if (!str) return null;
  return str;
}

/**
 * Teaser derivation according to Fallback rule F4:
 * - All whitespace sequences (including newlines) converted to a single space, trimmed.
 * - Up to 160 codepoints returned unchanged.
 * - Otherwise truncated to 159 codepoints, rewound to last space if after index 96, and '…' appended.
 */
export function toTeaser(text, max = TEASER_MAX) {
  if (!text || typeof text !== 'string') return '';
  const collapsed = text.replace(/\s+/g, ' ').trim();
  const codepoints = Array.from(collapsed);
  if (codepoints.length <= max) {
    return collapsed;
  }

  const targetLen = max - 1;
  const slice159 = codepoints.slice(0, targetLen).join('');
  const lastSpaceIdx = slice159.lastIndexOf(' ');
  if (lastSpaceIdx >= 96) {
    return slice159.slice(0, lastSpaceIdx) + '…';
  }
  return slice159 + '…';
}

/**
 * Checks whether text contains HTML tags.
 * Allows math/comparisons like "<5 Ampere" and "< 60 °C".
 */
export function containsHtmlTags(str) {
  if (!str) return false;
  return /<\s*\/?\s*[A-Za-z!?]/.test(str);
}

/**
 * Maps API errors / HTTP status codes to stable German UI texts (SPEC-P §12).
 */
export function mapProductEditError(err) {
  if (!err) return PRODUCT_EDIT_TEXTS.ERR_NETWORK;
  const status = Number(err.status || err.statusCode || 0);
  const code = String(err.details?.code || err.code || '');
  const rawMsg = typeof err === 'string' ? err : (err.message || '');

  if (code === 'NAME_TOO_LONG' || rawMsg.includes('maximal 80 Zeichen')) return PRODUCT_EDIT_TEXTS.ERR_NAME_TOO_LONG;
  if (code === 'DESCRIPTION_TOO_LONG' || rawMsg.includes('maximal 5.000 Zeichen') || rawMsg.includes('maximal 5000 Zeichen')) return PRODUCT_EDIT_TEXTS.ERR_DESCRIPTION_TOO_LONG;
  if (code === 'HTML_NOT_ALLOWED' || rawMsg.includes('HTML')) return PRODUCT_EDIT_TEXTS.ERR_HTML_NOT_ALLOWED;

  if (status === 401 || code === 'UNAUTHORIZED' || rawMsg.includes('Sitzung ist abgelaufen')) return PRODUCT_EDIT_TEXTS.ERR_401;
  if (status === 403 || code === 'FORBIDDEN' || rawMsg.includes('Berechtigung')) return PRODUCT_EDIT_TEXTS.ERR_403;
  if (status === 404 || code === 'PRODUCT_NOT_FOUND' || code === 'TENANT_NOT_FOUND') {
    return PRODUCT_EDIT_TEXTS.ERR_404;
  }
  if (status === 405 || code === 'METHOD_NOT_ALLOWED') {
    return PRODUCT_EDIT_TEXTS.ERR_405;
  }
  if (status === 408) {
    return PRODUCT_EDIT_TEXTS.ERR_408;
  }
  if (status === 409 || code === 'CONFLICT') {
    return PRODUCT_EDIT_TEXTS.ERR_409;
  }
  if (status === 413) {
    return PRODUCT_EDIT_TEXTS.ERR_413;
  }
  if (status === 415) {
    return PRODUCT_EDIT_TEXTS.ERR_415;
  }
  if (status === 422 || code === 'UNPROCESSABLE_ENTITY') {
    return PRODUCT_EDIT_TEXTS.ERR_422;
  }
  if (status === 429 || code === 'RATE_LIMITED') {
    return PRODUCT_EDIT_TEXTS.ERR_429;
  }
  if (status === 400 || status === 405) {
    return PRODUCT_EDIT_TEXTS.ERR_400_DEFAULT;
  }

  return PRODUCT_EDIT_TEXTS.ERR_NETWORK;
}

/**
 * Client API: Fetch product display metadata for admin (GET /api/admin/tenants/{tenant}/products/{productId})
 */
export async function getAdminProductDisplay(productId, tenantId) {
  if (!productId || !tenantId) throw new Error('productId and tenantId are required');
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/products/${encodeURIComponent(productId)}`);
}

/**
 * Client API: Update product display metadata for admin (PATCH /api/admin/tenants/{tenant}/products/{productId})
 */
export async function updateAdminProductDisplay(productId, tenantId, payload = {}) {
  if (!productId || !tenantId) throw new Error('productId and tenantId are required');
  return apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}/products/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: payload,
  });
}
